import { useState } from 'react';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useWishlist } from '@/hooks/useWishlist';
import { GuestIdentityModal } from '@/components/GuestIdentityModal';
import { captureLead } from '@/lib/analytics';

interface WishlistButtonProps {
  tourId: string;
  tourName: string;
  tourDescription: string;
  tourImage: string;
  tourHref: string;
}

export default function WishlistButton({ tourId, tourName, tourDescription, tourImage, tourHref }: WishlistButtonProps) {
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { toast } = useToast();
  const [isPending, setIsPending] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Helper to get stored identity
  const getStoredIdentity = () => {
    const stored = localStorage.getItem('guest_identity');
    if (stored) return JSON.parse(stored);
    return null;
  };

  const handleWishlistAdd = (name?: string, email?: string) => {
    setIsPending(true);
    try {
      // If we got fresh details from modal, save them first
      let identity = { name, email };
      if (name && email) {
        localStorage.setItem('guest_identity', JSON.stringify({ name, email }));

        // Shoot email over to Web3Forms using captureLead directly from here
        captureLead({
          email: email,
          name: name,
          source: `Wishlist Modal`,
          message: `This user just saved the "${tourName}" to their personal Wishlist! They are highly interested in this specific tour.`
        });
      } else {
        // Otherwise try to use stored identity
        const stored = getStoredIdentity();
        if (stored) identity = stored;
      }

      addToWishlist({
        id: tourId,
        tourId: tourId,
        name: tourName,
        description: tourDescription,
        image: tourImage,
        href: tourHref
      }, identity.name, identity.email);

    } catch (error) {
      // Error handled in hook mainly
    } finally {
      setIsPending(false);
      setIsModalOpen(false);
    }
  };

  const handleClick = () => {
    if (isInWishlist(tourId)) {
      setIsPending(true);
      removeFromWishlist(tourId);
      setIsPending(false);
    } else {
      // Check if we already have identity
      const identity = getStoredIdentity();
      if (identity) {
        handleWishlistAdd(identity.name, identity.email);
      } else {
        setIsModalOpen(true);
      }
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="lg"
        className="w-full bg-white/10 backdrop-blur-sm hover:bg-white/20 text-primary"
        onClick={handleClick}
        disabled={isPending}
      >
        <Heart
          className={`h-5 w-5 mr-2 ${isInWishlist(tourId) ? 'fill-primary' : ''}`}
        />
        {isInWishlist(tourId) ? 'Remove from Wishlist' : 'Add to Wishlist'}
      </Button>

      <GuestIdentityModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={(name, email) => handleWishlistAdd(name, email)}
      />
    </>
  );
}